import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp, runTransaction } from '../lib/fsCompat';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { claimBetaCounter, BETA_VIP_DAYS } from '../utils/betaReward'; // TEMP Beta — nach Beta entfernen

// Owner-Status kommt aus dem Firebase-Custom-Claim `owner: true` (gesetzt via
// scripts/set-owner-claim.mjs) — KEINE hartkodierte UID mehr im Bundle. Dient nur
// dem UI/UX; die echte Durchsetzung steht in firestore.rules (isAppOwner()).

// Zeichen ohne optische Verwechsler (kein I/O/0/1)
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
function generateVipCode() {
  const block = () => Array.from({ length: 4 }, () =>
    CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]).join('');
  return `RED-VIP-${block()}-${block()}`;
}

// Eigener Fehlertyp, damit unlockWithCode einen sauberen Grund liefern kann
class RedeemError extends Error {
  constructor(reason) { super(reason); this.reason = reason; }
}

const MIGRATION_FLAG_KEY = 'red_plan_migrated_v1';

// TX-Tracking ist für ALLE Tarife unbegrenzt — Erfassen/Importieren wird nie
// blockiert (Strategie 2026-06-03: Output-Gating statt Input-Limit; der Wedge
// ist "voller Umfang gratis"). Die Bezahlschranke greift künftig am OUTPUT
// (Report ≤ 500 TX, KI-Upload, Börsen-API) über eigene Konstanten — NICHT hier.
// Beide Input-Gates in App.js sind durch Number.isFinite() geschützt und werden
// dadurch automatisch zur No-Op.
export const TIER_LIMITS = {
  free:    { txPerYear: Infinity },
  premium: { txPerYear: Infinity },
  vip:     { txPerYear: Infinity },
};

// Preise (zentral, damit Pricing-Screen + Reports konsistent bleiben)
export const PRICING = {
  report:  { price: 19,  unit: 'einmalig / Steuerjahr', label: 'Report' },
  premium: { price: 49,  unit: 'pro Jahr',              label: 'Premium' },
  vip:     { price: 299, unit: 'pro Jahr',              label: 'VIP' },
};

function normalizeAccount(raw) {
  const tier = raw?.tier === 'premium' || raw?.tier === 'vip' ? raw.tier : 'free';
  return {
    tier,
    tierExpiresAt: raw?.tierExpiresAt ?? null,
    reportPurchases: raw?.reportPurchases || {},
    vipCodeRedeemed: raw?.vipCodeRedeemed || null,
    source: raw?.source || null,
  };
}

const DEFAULT_ACCOUNT = {
  tier: 'free',
  tierExpiresAt: null,
  reportPurchases: {},
  vipCodeRedeemed: null,
  source: null,
};

const UserPlanContext = createContext(null);

export function UserPlanProvider({ children }) {
  const { user } = useAuth();
  const [account, setAccount] = useState(DEFAULT_ACCOUNT);
  const [accountLoading, setAccountLoading] = useState(true);
  const [isOwnerClaim, setIsOwnerClaim] = useState(false);

  // ── Owner-Status aus dem ID-Token-Custom-Claim lesen ───────────────────────
  // (Backoffice-Owner; per scripts/set-owner-claim.mjs gesetzt. Greift erst nach
  // einem Token-Refresh / Re-Login.)
  useEffect(() => {
    if (!user) { setIsOwnerClaim(false); return; }
    let cancelled = false;
    // force-refresh (true): holt ein FRISCHES Token, damit ein gerade gesetzter
    // owner-Claim sofort greift — ohne dass sich der User neu einloggen muss.
    // (Behebt "Owner-Knopf mal da, mal weg", v.a. auf Mobile mit altem Token.)
    user.getIdTokenResult(true)
      .then((res) => { if (!cancelled) setIsOwnerClaim(res?.claims?.owner === true); })
      .catch(() => { if (!cancelled) setIsOwnerClaim(false); });
    return () => { cancelled = true; };
  }, [user]);

  // ── Firestore-Listener auf users/{uid}/account/state ───────────────────────
  useEffect(() => {
    if (!user) {
      setAccount(DEFAULT_ACCOUNT);
      setAccountLoading(false);
      return;
    }
    setAccountLoading(true);
    const ref = doc(db, 'users', user.uid, 'account', 'state');
    const unsub = onSnapshot(
      ref,
      async (snap) => {
        if (!snap.exists()) {
          // Erste Anmeldung oder noch nicht migriert.
          // HINWEIS: Der alte localStorage-Plan (`red_plan`) war client-seitig frei
          // manipulierbar und NICHT zahlungs-gedeckt. Er wird deshalb NICHT mehr als
          // premium/vip übernommen (ein solcher tier-Write würde von den Firestore-
          // Rules ohnehin abgelehnt). Legitime Freischaltung läuft ausschließlich über
          // einen eingelösten VIP-Code. Wir setzen nur das Flag, damit die Prüfung
          // nicht erneut läuft.
          if (!localStorage.getItem(MIGRATION_FLAG_KEY)) {
            localStorage.setItem(MIGRATION_FLAG_KEY, 'true');
          }
          // BETA (TEMP — nach der Beta entfernen): kommt der User über den Beta-Button
          // (redcrypto.de → app.redcrypto.de/?beta=1), zählen wir den öffentlichen
          // Zähler hoch. VIP gibt es NUR, wenn der Owner das Konto freigeschaltet hat
          // (betaGrants/{uid} existiert) — diesen Marker kann der User nicht selbst
          // anlegen → kein Self-Grant. Ohne Grant bleibt das Konto free.
          const isBeta = typeof window !== 'undefined'
            && new URLSearchParams(window.location.search).get('beta') === '1';
          let betaExtra = {};
          if (isBeta) {
            await claimBetaCounter(user.uid);
            try {
              const grantSnap = await getDoc(doc(db, 'betaGrants', user.uid));
              if (grantSnap.exists()) {
                betaExtra = { tier: 'vip', source: 'beta', tierExpiresAt: Date.now() + BETA_VIP_DAYS * 24 * 60 * 60 * 1000 };
              }
            } catch (err) {
              console.warn('[UserPlan] beta-grant check failed:', err?.message);
            }
          }
          const migrated = { ...DEFAULT_ACCOUNT, ...betaExtra };
          try {
            await setDoc(ref, {
              ...migrated,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          } catch (err) {
            console.warn('[UserPlan] initial account doc write failed:', err?.message);
          }
          setAccount(migrated);
          setAccountLoading(false);
          return;
        }
        const norm = normalizeAccount(snap.data());
        // Auto-Ablauf: abgelaufenes VIP/Premium einmalig auf Free zurückstufen.
        // (tier === 'free' schließt eine Schleife aus — danach greift die Bedingung nicht mehr.)
        if (norm.tier !== 'free' && norm.tierExpiresAt && Number(norm.tierExpiresAt) < Date.now()) {
          try {
            await setDoc(ref, { tier: 'free', source: 'expired', updatedAt: serverTimestamp() }, { merge: true });
          } catch (err) {
            console.warn('[UserPlan] expiry downgrade write failed:', err?.message);
          }
          setAccount({ ...norm, tier: 'free', source: 'expired' });
          setAccountLoading(false);
          return;
        }
        // BETA (TEMP): kommt ein noch-free Konto über den Beta-Link (?beta=1) zurück
        // und hat der Owner es inzwischen freigeschaltet (betaGrants/{uid}), heben wir
        // es nachträglich auf VIP — deckt „Owner schaltet erst nach dem ersten Login
        // frei" ab. Danach ist tier=vip → die Bedingung greift nicht erneut.
        const isBetaReturn = typeof window !== 'undefined'
          && new URLSearchParams(window.location.search).get('beta') === '1';
        if (isBetaReturn && norm.tier === 'free') {
          try {
            const grantSnap = await getDoc(doc(db, 'betaGrants', user.uid));
            if (grantSnap.exists()) {
              const expiresAt = Date.now() + BETA_VIP_DAYS * 24 * 60 * 60 * 1000;
              await setDoc(ref, { tier: 'vip', source: 'beta', tierExpiresAt: expiresAt, updatedAt: serverTimestamp() }, { merge: true });
              setAccount({ ...norm, tier: 'vip', source: 'beta', tierExpiresAt: expiresAt });
              setAccountLoading(false);
              return;
            }
          } catch (err) {
            console.warn('[UserPlan] beta-grant upgrade failed:', err?.message);
          }
        }
        setAccount(norm);
        setAccountLoading(false);
      },
      (err) => {
        console.error('[UserPlan] Firestore-Listener Fehler:', err);
        setAccountLoading(false);
      }
    );
    return unsub;
  }, [user]);

  // ── Tier setzen (lokal optimistisch + Firestore) ──────────────────────────
  // ACHTUNG: Solange wir keinen Stripe-Webhook haben, kann der Client schreiben.
  // Sobald Stripe live ist, wird das nur noch via Server-Webhook erlaubt.
  const setPlan = useCallback(async (newPlan, extra = {}) => {
    const safe = newPlan === 'premium' || newPlan === 'vip' ? newPlan : 'free';
    setAccount((prev) => ({ ...prev, tier: safe, ...extra }));
    if (!user) return;
    const ref = doc(db, 'users', user.uid, 'account', 'state');
    try {
      await setDoc(
        ref,
        {
          tier: safe,
          ...extra,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn('[UserPlan] setPlan write failed:', err?.message);
    }
  }, [user]);

  const unlockWithCode = useCallback(async (code) => {
    const upper = (code || '').trim().toUpperCase();
    if (!upper) return { ok: false, reason: 'empty' };
    if (!user) return { ok: false, reason: 'auth' };

    const codeRef = doc(db, 'vipCodes', upper);
    let durationDays = 365;
    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(codeRef);
        if (!snap.exists()) throw new RedeemError('invalid');
        const data = snap.data() || {};
        // Schon von jemand anderem eingelöst → tot (Weitergabe läuft hier ins Leere).
        if (data.redeemedBy && data.redeemedBy !== user.uid) throw new RedeemError('used');
        durationDays = Number(data.durationDays) || 365;
        // Erst-Einlösung: an dieses Konto binden — trifft exakt die Security-Rule.
        if (!data.redeemedBy) {
          tx.update(codeRef, { redeemedBy: user.uid, redeemedAt: serverTimestamp() });
        }
      });
    } catch (e) {
      if (e instanceof RedeemError) return { ok: false, reason: e.reason };
      console.warn('[UserPlan] redeem tx failed:', e?.message);
      return { ok: false, reason: 'error' };
    }

    const expiresAt = Date.now() + durationDays * 24 * 60 * 60 * 1000;
    await setPlan('vip', { vipCodeRedeemed: upper, source: 'vip_code', tierExpiresAt: expiresAt });
    return { ok: true };
  }, [user, setPlan]);

  // Owner-only: neuen Einmal-VIP-Code erzeugen und in Firestore anlegen.
  const createVipCode = useCallback(async ({ note = '', durationDays = 365 } = {}) => {
    if (!user || !isOwnerClaim) return { ok: false, reason: 'forbidden' };
    const code = generateVipCode();
    try {
      await setDoc(doc(db, 'vipCodes', code), {
        redeemedBy: null,
        redeemedAt: null,
        note: note || null,
        durationDays: Number(durationDays) || 365,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn('[UserPlan] createVipCode failed:', e?.message);
      return { ok: false, reason: 'error' };
    }
    return { ok: true, code };
  }, [user, isOwnerClaim]);

  // ── Report-Käufe (für €19 One-Time pro Steuerjahr) ────────────────────────
  const markReportPurchased = useCallback(async (year) => {
    if (!user || !year) return;
    const yearKey = String(year);
    setAccount((prev) => ({
      ...prev,
      reportPurchases: { ...prev.reportPurchases, [yearKey]: true },
    }));
    const ref = doc(db, 'users', user.uid, 'account', 'state');
    try {
      await setDoc(
        ref,
        {
          reportPurchases: { [yearKey]: true },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.warn('[UserPlan] markReportPurchased failed:', err?.message);
    }
  }, [user]);

  const hasReportForYear = useCallback((year) => {
    if (!year) return false;
    return !!account.reportPurchases?.[String(year)];
  }, [account.reportPurchases]);

  // ── Abgeleitete Werte ─────────────────────────────────────────────────────
  const value = useMemo(() => {
    const tier = account.tier;
    const isPremium = tier === 'premium' || tier === 'vip';
    const isVip = tier === 'vip';
    const isOwner = isOwnerClaim;
    const txYearLimit = TIER_LIMITS[tier]?.txPerYear ?? TIER_LIMITS.free.txPerYear;

    return {
      // Backward-Compat (alter Code)
      plan: tier,
      setPlan,
      isPremium,
      unlockWithCode,
      createVipCode,
      isOwner,

      // Neue API
      tier,
      isVip,
      tierLoading: accountLoading,
      tierExpiresAt: account.tierExpiresAt,
      tierSource: account.source,
      txYearLimit,

      // Report-Kauf-API
      reportPurchases: account.reportPurchases,
      hasReportForYear,
      markReportPurchased,

      // Kann diese User für das angegebene Jahr Reports exportieren?
      canExportReport: (year) => isPremium || hasReportForYear(year),
    };
  }, [account, accountLoading, user, isOwnerClaim, setPlan, unlockWithCode, createVipCode, hasReportForYear, markReportPurchased]);

  return (
    <UserPlanContext.Provider value={value}>
      {children}
    </UserPlanContext.Provider>
  );
}

export function useUserPlan() {
  return useContext(UserPlanContext);
}
